/**
 * Allowlist sanitiser for admin-authored public record templates.
 *
 * Public record layouts are mounted into the app's own DOM rather than a
 * sandboxed iframe (spec D-4), because iframe content is invisible to crawlers
 * and to the OG/Twitter preview path — and SEO is the point of public pages.
 * That choice removes the iframe's isolation, so this module is the boundary
 * that replaces it.
 *
 * The filter is STRUCTURAL — it walks the parsed tree and inspects typed nodes,
 * rather than running regexes over source text. Textual filtering of HTML is
 * not defensible: `.replace()` is single-pass, so surviving fragments can
 * re-concatenate into the construct that was stripped (`<scr<script>ipt>`), and
 * attribute-name matching would have to hard-code a case despite HTML
 * attribute names being case-insensitive. Once DOMParser has run, none of that
 * applies — the parser has already tokenised tags and attributes into a tree
 * with no raw text left to reassemble. This mirrors the reasoning in
 * scopedCustomCss.js, which reaches the same conclusion for CSS.
 *
 * Parse with DOMParser (an inert document: no scripts run, no subresources are
 * fetched) and sanitise BEFORE the tree is ever adopted into the live document.
 *
 * Allowlist, not blocklist: an unknown tag is unwrapped and an unknown
 * attribute dropped, so a construct nobody thought of fails closed.
 */

export const ALLOWED_TAGS = new Set([
  'div', 'section', 'article', 'aside', 'header', 'footer', 'main', 'nav',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'span', 'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark', 'br', 'hr',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'a', 'img', 'figure', 'figcaption', 'picture', 'source',
  'blockquote', 'pre', 'code', 'time', 'address', 'button', 'label',
]);

// Elements removed WITH their content. Unwrapping these would leak the script
// body or stylesheet source as visible page text.
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'select',
  'textarea', 'template', 'noscript', 'base', 'link', 'meta', 'svg', 'math',
]);

const ALLOWED_ATTRS = new Set([
  'class', 'id', 'href', 'src', 'alt', 'title', 'target', 'rel',
  'width', 'height', 'colspan', 'rowspan', 'datetime', 'loading', 'type',
  // Carried only by the hidden payload inputs permitted below; every other
  // element that could use `value` is already blocked.
  'value',
]);

const ALLOWED_DATA_ATTR_PREFIX = 'data-arcee-';

const URL_ATTRS = new Set(['href', 'src']);

// Not global: .test() on a /g/ regex advances lastIndex between calls.
const HAS_PLACEHOLDER_RE = /\{\{[^}]*\}\}/;

// Anchored to the start; control characters and whitespace are stripped first
// so "  JaVa\tScRiPt:" cannot slip past.
const SAFE_URL_RE = /^(?:https?:|mailto:|tel:|#|\/(?!\/))/i;

/**
 * True when a URL attribute value is safe to keep.
 *
 * Whitespace and C0 control characters are removed before matching because
 * browsers ignore them when resolving a URL scheme — "java\tscript:alert(1)"
 * navigates exactly as "javascript:alert(1)" does.
 */
export function isSafeUrl(value) {
  if (typeof value !== 'string') return false;
  // eslint-disable-next-line no-control-regex
  const collapsed = value.replace(/[ - ]/g, '');
  if (collapsed === '') return false;
  return SAFE_URL_RE.test(collapsed);
}

function isAllowedAttr(name) {
  const lower = name.toLowerCase();
  if (lower.startsWith('on')) return false;          // every event handler
  if (lower === 'style') return false;               // url()/expression() surface
  if (lower.startsWith(ALLOWED_DATA_ATTR_PREFIX)) return true;
  return ALLOWED_ATTRS.has(lower);
}

/**
 * The single, narrow exception to "no form controls".
 *
 * A template may carry hidden values into an action's payload —
 * `<input type="hidden" data-arcee-payload="source" value="…">`. Only that
 * exact shape survives: type must be hidden, and the payload marker must be
 * present. A visible input, a password box, or a hidden input without the
 * marker is still removed.
 *
 * This is safe because it cannot collect anything from the visitor and cannot
 * submit anywhere — `<form>` is still blocked, so there is nothing to post to.
 * Whether the value is honoured at all is decided server-side, where it may
 * only fill a column the button explicitly offers.
 */
function isAllowedHiddenPayloadInput(el) {
  return el.tagName.toLowerCase() === 'input'
    && (el.getAttribute('type') || '').toLowerCase() === 'hidden'
    && el.hasAttribute('data-arcee-payload');
}

function sanitiseElement(el) {
  // Copy the list first — removeAttribute mutates the live NamedNodeMap.
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name;
    if (!isAllowedAttr(name)) {
      el.removeAttribute(name);
      continue;
    }
    // A URL that still contains a {{ }} placeholder cannot be judged yet — the
    // record supplies part of it. Stripping it here would delete every
    // src="{{ record.image }}" before it could ever be filled in. It is kept,
    // and renderRecordTemplate re-checks the FINISHED value against this same
    // allowlist, so nothing unsafe survives either way.
    if (URL_ATTRS.has(name.toLowerCase())
        && !HAS_PLACEHOLDER_RE.test(attr.value)
        && !isSafeUrl(attr.value)) {
      el.removeAttribute(name);
    }
  }

  // An author-supplied target="_blank" without noopener hands the opened page
  // a window.opener handle back into this origin.
  if (el.tagName.toLowerCase() === 'a' && el.getAttribute('target') === '_blank') {
    el.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Sanitises `root` IN PLACE. Call on a DOMParser-produced tree, before it is
 * adopted into the live document.
 */
export function sanitiseTemplateTree(root) {
  if (!root) return root;

  // Snapshot before mutating: unwrapping moves nodes, which would invalidate a
  // live NodeList mid-walk.
  for (const el of Array.from(root.querySelectorAll('*'))) {
    // Skip nodes already detached by an ancestor's removal.
    if (!root.contains(el)) continue;

    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      if (isAllowedHiddenPayloadInput(el)) {
        sanitiseElement(el);
        continue;
      }
      el.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep the author's content, discard the unknown wrapper.
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }

    sanitiseElement(el);
  }

  return root;
}
